import moment from 'moment';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef
} from 'react';

import { formattedDateTime } from '../../../../utils/date';
import doFetch from '../../../../api/doFetch';

const context = createContext({});
const MODULES = {
  CAPTURE_CODE: { name: 'EXIF verification', rank: 0 },
  VIN: { name: 'VIN check', rank: 1, tooltip: true },
  GEOPOSITION: { name: 'GPS location', rank: 2, tooltip: true },
  DATETIME: { name: 'Timestamp frame', rank: 3, tooltip: true },
  VISUAL_MATCH: { name: 'Al VIN-aware', rank: 4 },
  ORIGINAL_PHOTO: { name: 'Al authenticity', rank: 5 }
};

export const useDownloadFiles = (selected, byId = true) => {
  return async () => {
    doFetch(`/vehicles/photos/download`, {
      method: 'POST',
      body: byId
        ? JSON.stringify({ ids: selected.map((i) => i.id) })
        : JSON.stringify({ urls: selected.map((i) => i.url) }),
      responseType: 'blob'
    })
      .then((response) => response.blob())
      .then((response) => {
        const file = new Blob([response], { type: 'application/octet-stream' });
        const fileURL = URL.createObjectURL(file);
        var a = document.createElement('a');
        a.href = fileURL;
        a.download = `files.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch((error) => {
        alert(error);
      });
  };
};

export default function ({ vehicle, mutateVehicle, children }) {
  const [statuses, setStatuses] = useState();
  const intervalRef = useRef();

  useEffect(() => {
    const progress = { LOTFLO: false, HOMENET: false };

    const statuses = (vehicle?.statuses || []).reduce(
      (result, status) => {
        if (!status?.module) {
          progress[status.imageProvider] = true;
        }

        status.modeleName = MODULES[status.module]?.name;
        status.className = status.status ? 'successful' : 'unsuccessful';
        status.confidenceText = Math.floor(status.confidence * 100) + '%';
        status.rank = MODULES[status.module]?.rank;

        let exif;
        try {
          exif = JSON.parse(status.exif);
        } catch {}

        switch (status.module) {
          case 'CAPTURE_CODE':
            status.tooltip = status.status
              ? 'Capture Code exists and valid'
              : 'Invalid Capture Code';
            break;
          case 'VIN':
            status.tooltip = vehicle.vin;
            break;
          case 'GEOPOSITION':
            status.tooltip = `dealership: ${vehicle.dealership.latitude} ${vehicle.dealership.longitude}`;
            if (exif?.gpsPosition) status.tooltip += `\n image: ${exif.gpsPosition}`;
            break;
          case 'DATETIME':
            if (exif?.createdOn) {
              const date = moment(exif.createdOn, 'YYYY:MM:DD HH:mm:ss.SSS').format(
                'MM/DD/y hh:mm A'
              );
              status.tooltip = `created at: ${date}`;
            } else {
              status.tooltip = 'Timestamp is missing';
            }
            break;
          case 'VISUAL_MATCH':
            if (status.failReason) {
              status.tooltip = status.failReason;
            } else if (status.confidence) {
              status.tooltip = `Confidence: ${status.confidenceText || ''}`;
            }
            break;
          default:
            if (status.confidence) status.tooltip = `Confidence: ${status.confidenceText || ''}`;
            break;
        }
        if (status.tooltip) status.className += ` tooltip ${status.module}`;
        status.verificationDate = status.verificationDate
          ? formattedDateTime(new Date(status.verificationDate * 1000))
          : '';

        result[status.imageProvider].push(status);
        return result;
      },
      {
        LOTFLO: [],
        HOMENET: []
      }
    );

    if (intervalRef.current?.i) {
      if (!progress[intervalRef.current.n]) {
        clearInterval(intervalRef.current.i);
        intervalRef.current = null;
      }
    }
    setStatuses({
      LOTFLO: {
        title: 'LotFlo Image',
        progress: progress.LOTFLO,
        list: statuses.LOTFLO.sort((a, b) => a.rank - b.rank),
        date: statuses.LOTFLO.find((s) => Boolean(s.verificationDate))?.verificationDate
      },
      HOMENET: {
        title: 'HomeNet Image',
        progress: progress.HOMENET,
        list: statuses.HOMENET.sort((a, b) => a.rank - b.rank),
        date: statuses.HOMENET.find((s) => Boolean(s.verificationDate))?.verificationDate
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?.statuses]);

  const refresh = useCallback(
    async (provider) => {
      setStatuses((statuses) => ({
        ...statuses,
        [provider]: {
          ...statuses[provider],
          progress: true,
          list: [true]
        }
      }));

      await doFetch(
        `/vehicles/dealership/${vehicle?.dealership?.hmsId}/vehicle/${vehicle?.vin}/image/reverify`,
        {
          method: 'POST',
          body: JSON.stringify({ provider })
        }
      );

      mutateVehicle();

      intervalRef.current = {
        n: provider,
        i: setInterval(() => mutateVehicle(), 20000)
      };
    },
    [setStatuses, vehicle, mutateVehicle]
  );

  useEffect(() => {
    return () => {
      if (intervalRef.current?.i) clearInterval(intervalRef.current?.i);
    };
  }, []);

  const contextValue = useMemo(() => ({ statuses, refresh }), [statuses, refresh]);

  return (
    <>{contextValue && <context.Provider value={contextValue}>{children}</context.Provider>}</>
  );
}

export const useVerificationStatuses = () => {
  return useContext(context)?.statuses;
};

export const useVerificationRefresh = () => {
  const { refresh } = useContext(context);

  return useCallback(
    (provider) => {
      refresh(provider);
    },
    [refresh]
  );
};
